import { Component } from 'vue-property-decorator';
import StudioModalMobile from '@/components/modals/StudioModalMobile.vue';
import EditorBase from '../EditorBase';
import { Tabs, Button } from 'streamlabs-beaker';
import { ITab } from '@/helpers/utils';
import DropZone from '@/components/inputs/DropZone.vue';
import UploadApi from '@/apis/upload-api';
import { IFile } from '@/helpers/upload';
import { EModalName } from '@/helpers/modal';
import NotificationHelper from '@/helpers/notification';
import ComponentHelper from '@/helpers/editor';
import { cloneDeep } from 'lodash';

type TUploadManagerTabs = 'stock' | 'uploads';
enum EUploadManagerTabs {
  stock = 'stock',
  uploads = 'uploads',
}

@Component({
  components: {
    StudioModalMobile,
    STabs: Tabs,
    SButton: Button,
    DropZone,
  },
})
export default class UploadManagerMobile extends EditorBase {
  files: IFile[] = null;
  selectedFile: string = null;
  shouldReplaceExisting: boolean = false;

  async beforeOpen({ params }) {
    if (params?.shouldReplaceExisting) {
      this.shouldReplaceExisting = params.shouldReplaceExisting;
    }
  }
  async mounted() {
    const response = await UploadApi.getFile();

    if (response) {
      this.files = response.files;
    }
  }

  get modalName(): string {
    return EModalName.uploadManagerMobile;
  }

  get tabs(): ITab[] {
    return [
      { name: EUploadManagerTabs.stock.toUpperCase(), value: EUploadManagerTabs.stock },
      { name: EUploadManagerTabs.uploads.toUpperCase(), value: EUploadManagerTabs.uploads },
    ];
  }

  get EUploadManagerTabs() {
    return EUploadManagerTabs;
  }

  get imageUrls(): string[] {
    return [
      'https://cdn.streamlabsstudio.com/static/imgs/stock/brb.png',
      'https://cdn.streamlabsstudio.com/static/imgs/stock/starting-soon.png',
      'https://cdn.streamlabsstudio.com/static/imgs/stock/ending.png',
      'https://cdn.streamlabsstudio.com/static/imgs/stock/webcam-border.png',
      'https://cdn.streamlabs.com/static/imgs/mobile/backgrounds/leaf.png',
      'https://cdn.streamlabs.com/static/imgs/mobile/backgrounds/candy-cane.png',
      'https://cdn.streamlabs.com/static/imgs/mobile/backgrounds/warhol.png',
      'https://cdn.streamlabs.com/static/imgs/mobile/backgrounds/matrix.png',
      'https://cdn.streamlabs.com/static/imgs/mobile/backgrounds/bubbles.png',
      'https://cdn.streamlabs.com/static/imgs/mobile/backgrounds/jungle.png',
    ];
  }

  getPlaceholderImageName(link): string {
    return link.split('/').pop();
  }

  get existingFileNames() {
    if (!this.files) return [];
    return this.files.map((file) => this.getFileName(file));
  }

  getFileName(file: IFile): string {
    if (file) {
      return file.href.split('/').slice(-1).pop();
    }

    return '';
  }

  hideModal() {
    this.$modal.hide(this.modalName);
  }

  onUploadSuccess(files: IFile[]) {
    if (!files.length) return;

    this.files = (this.files || []).concat(files);

    this.selectedFile = files[0].href;
  }

  async removeImage(file: IFile) {
    const fileName = this.getFileName(file);
    const response = await UploadApi.removeFile(fileName);

    if (response.success) this.files = this.files.filter((el) => el.href !== file.href);
  }

  fileOnClick(href) {
    this.selectedFile = href;
  }

  beforeClose() {
    this.selectedFile = null;
  }

  async updateComponent() {
    if (!this.selectedFile) {
      NotificationHelper.error('Please select an image to continue.');
      return;
    }

    if (this.shouldReplaceExisting) {
      this.removeComponent();
    }

    const imageSizeAndAspectRatio = await ComponentHelper.getImageOriginalSizeAndAspectRatio(this.selectedFile, this.projectWidth);

    const componentData = {
      value: this.selectedFile,
      w: imageSizeAndAspectRatio.width,
      h: imageSizeAndAspectRatio.height,
      aspectRatio: imageSizeAndAspectRatio.aspectRatio,
    };

    const components = cloneDeep(this.components);

    const newComponent = { ...ComponentHelper.defaultImageSettings, ...componentData };

    components.unshift(newComponent);
    this.components = components;
    this.selectedComponent = newComponent;

    this.onComponentUpdate({ ...this.selectedComponent, ...componentData });

    this.hideModal();
    this.preventComponentDeactivate = false;
  }
}
